<script setup>
import { ref } from 'vue';
import { useStore } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';

import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  bot: {
    type: Object,
    required: true,
  },
});

const emit = defineEmits(['imported']);

const { t } = useI18n();
const store = useStore();

const fileInput = ref(null);
const isImporting = ref(false);
const result = ref(null);

const pickFile = () => fileInput.value?.click();

const importCsv = async file => {
  if (!file) return;
  isImporting.value = true;
  result.value = null;
  try {
    const data = await store.dispatch('aiBotAnswers/import', {
      botId: props.bot.id,
      file,
    });
    if (data) {
      result.value = data;
      emit('imported', data);
    }
  } finally {
    isImporting.value = false;
    if (fileInput.value) fileInput.value.value = '';
  }
};

const onFileInputChange = event => importCsv(event.target.files?.[0]);
</script>

<template>
  <div class="flex flex-col gap-3" data-testid="answer-import">
    <p class="text-sm text-n-slate-11">
      {{ $t('AI_AGENT.ANSWERS.IMPORT.HELP') }}
    </p>
    <div class="flex items-center gap-2">
      <Button
        size="sm"
        faded
        slate
        icon="i-lucide-upload"
        :label="t('AI_AGENT.ANSWERS.IMPORT.BROWSE')"
        :is-loading="isImporting"
        data-testid="import-browse"
        @click="pickFile"
      />
      <input
        ref="fileInput"
        type="file"
        class="hidden"
        accept=".csv,text/csv"
        data-testid="import-file-input"
        @change="onFileInputChange"
      />
    </div>
    <div
      v-if="result"
      class="flex flex-col gap-1 rounded-lg bg-n-solid-2 px-3 py-2 text-sm text-n-slate-11"
      data-testid="import-result"
    >
      <span>{{
        t('AI_AGENT.ANSWERS.IMPORT.CREATED', { n: result.created ?? 0 })
      }}</span>
      <span>{{
        t('AI_AGENT.ANSWERS.IMPORT.SKIPPED', { n: result.skipped ?? 0 })
      }}</span>
      <span v-if="result.errors?.length" class="text-n-ruby-11">
        {{ t('AI_AGENT.ANSWERS.IMPORT.ERRORS', { n: result.errors.length }) }}
      </span>
    </div>
  </div>
</template>
